import { StateCreator } from "zustand";
import { supabase, Tables } from "@/lib/supabase";
import { SupabaseTableEnum } from "@/lib/supabase/supabaseTypes";
import { showNotification } from "../Central/selectors";
import { Logger } from "@/lib/logger/Logger";
import { JobStoreUnion } from ".";
import { produce } from "immer";

interface JobNotesState {
	jobNotes: Tables<"job_notes">[];
	jobNotesLoading: boolean;
}

export const initialJobNotesState: JobNotesState = {
	jobNotes: [],
	jobNotesLoading: false,
};

export interface JobNotesSlice extends JobNotesState {
	fetchJobNotes: (jobId: number) => Promise<void>;
	createNote: (jobId: number, content: string) => Promise<void>;
	updateNote: (noteId: number, content: string) => Promise<void>;
	deleteNote: (noteId: number) => Promise<void>;
	resetJobNotes: () => void;
}

export const createJobNotesStore: StateCreator<
	JobStoreUnion,
	[],
	[],
	JobNotesSlice
> = (set, get) => ({
	...initialJobNotesState,

	fetchJobNotes: async (jobId: number) => {
		if (!jobId) {
			Logger.error("fetchJobNotes called with null/undefined jobId");
			return;
		}

		set({ jobNotesLoading: true });

		const { data, error } = await supabase
			.from("job_notes")
			.select("*")
			.eq("job_id", jobId)
			.order("created_at", { ascending: false });

		if (error) {
			Logger.error(error, {}, "Error fetching job notes");
			showNotification({
				message: "Fehler beim Abrufen der Notizen",
				type: "error",
			});
			set({ jobNotesLoading: false });
			return;
		}

		set({
			jobNotes: data || [],
			jobNotesLoading: false,
		});
	},

	createNote: async (jobId: number, content: string) => {
		const { data: userData, error: userError } =
			await supabase.auth.getUser();

		if (userError || !userData.user) {
			showNotification({
				message: "Fehler beim Erstellen der Notiz",
				type: "error",
			});
			return;
		}

		const noteData = {
			job_id: jobId,
			content: { text: content },
			assignee_id: userData.user.id,
		};

		const { data, error } = await supabase
			.from("job_notes")
			.insert(noteData)
			.select()
			.single();

		if (error) {
			Logger.error(error, {}, "Error creating job note");
			showNotification({
				message: "Fehler beim Erstellen der Notiz",
				type: "error",
			});
			return;
		}

		set(
			produce((state) => {
				state.jobNotes.unshift(data);
			})
		);

		showNotification({
			message: "Notiz erstellt",
			type: "success",
		});
	},

	updateNote: async (noteId: number, content: string) => {
		if (!noteId) {
			Logger.error("updateNote called with null/undefined noteId");
			return;
		}

		// Optimistic update
		const previousNotes = get().jobNotes;

		set(
			produce((state) => {
				const noteIndex = state.jobNotes.findIndex(
					(n: Tables<"job_notes">) => n.id === noteId
				);
				if (noteIndex !== -1) {
					state.jobNotes[noteIndex].content = { text: content };
					state.jobNotes[noteIndex].modified_at =
						new Date().toISOString();
				}
			})
		);

		const { error } = await supabase
			.from("job_notes")
			.update({
				content: { text: content },
				modified_at: new Date().toISOString(),
			})
			.eq("id", noteId);

		if (error) {
			Logger.error(error, {}, "Error updating job note");
			showNotification({
				message: "Fehler beim Aktualisieren der Notiz",
				type: "error",
			});
			// Revert optimistic update
			set({ jobNotes: previousNotes });
			return;
		}

		showNotification({
			message: "Notiz aktualisiert",
			type: "success",
		});
	},

	deleteNote: async (noteId: number) => {
		if (!noteId) {
			Logger.error("deleteNote called with null/undefined noteId");
			return;
		}

		// Optimistic update
		const previousNotes = get().jobNotes;

		set(
			produce((state) => {
				state.jobNotes = state.jobNotes.filter(
					(n: Tables<"job_notes">) => n.id !== noteId
				);
			})
		);

		const { error } = await supabase
			.from("job_notes")
			.delete()
			.eq("id", noteId);

		if (error) {
			Logger.error(error, {}, "Error deleting job note");
			showNotification({
				message: "Fehler beim Löschen der Notiz",
				type: "error",
			});
			// Revert optimistic update
			set({ jobNotes: previousNotes });
			return;
		}

		showNotification({
			message: "Notiz gelöscht",
			type: "success",
		});
	},

	resetJobNotes: () => {
		set(initialJobNotesState);
	},
});
